import { apiSlice } from '@fly/store';

import {
  CheckRegistrationStatusRequest,
  CheckRegistrationStatusResponse,
  SignUpInvitationRegistrationRequest,
  SignUpProvider,
  SignUpRequest,
  SignUpResendVerificationCodeRequest,
  SignUpResolveSSOToken,
  SignUpResolveSSOTokenRequest,
  SignUpResolveSSOTokenResponse,
  SignUpTenantRegistrationRequest,
  SignUpVerifyCode,
  SignUpVerifyCodeRequest,
  SignUpVerifyCodeResponse,
} from '../types';
import { updateSignupState } from './signup.slice';

const getAccessToken = () => {
  try {
    const oidc = sessionStorage.getItem('oidc.default');
    const oidcData = oidc ? JSON.parse(oidc) : null;
    return oidcData?.tokens?.accessToken;
  } catch (error) {
    console.error('Error getting access token', error);
    return null;
  }
};

export const signupService = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    getUserInfo: builder.query<{ lastName: string; firstName: string }, string>(
      {
        query: (token) => ({
          api: 'user',
          url: '/user/v1',
          method: 'GET',
          config: {
            headers: { Authorization: `Bearer ${token}` },
            noToast: true,
          },
        }),
        transformResponse: (response: any) => ({
          lastName: response?.attributes?.last_name,
          firstName: response?.attributes?.first_name,
        }),
      }
    ),

    verifyInvitation: builder.mutation<void, string>({
      query: (invitationToken) => ({
        api: 'user',
        url: '/user/v1/validate-auth-verification-token?auth_flow=register',
        method: 'GET',
        config: {
          headers: { Authorization: `Bearer ${invitationToken}` },
          noToast: true,
        },
      }),
    }),

    checkRegistrationStatus: builder.mutation<
      CheckRegistrationStatusResponse,
      CheckRegistrationStatusRequest
    >({
      query: ({ email, registrationToken, provider }) => {
        const headers: Record<string, string> = {};

        if (provider === SignUpProvider.EMAIL) {
          headers['x-jfrog-registration-token'] = registrationToken;
        }

        if (
          provider === SignUpProvider.GITHUB ||
          provider === SignUpProvider.GOOGLE
        ) {
          headers['x-jfrog-sso-token'] = registrationToken;
          headers['x-jfrog-sso-origin'] = provider;
        }

        if (provider === SignUpProvider.ACCESS_TOKEN) {
          const accessToken = getAccessToken();
          if (accessToken) {
            headers['Authorization'] = `Bearer ${accessToken}`;
          }
        }

        return {
          api: 'auth',
          url: `/registration/tenant/registration/status?email=${encodeURIComponent(email)}`,
          method: 'GET',
          config: {
            headers,
            noToast: true,
            'axios-retry': {
              retries: 1,
            },
          },
        };
      },
    }),

    checkEmailAvailability: builder.mutation<void, string>({
      query: (email) => ({
        api: 'auth',
        url: '/registration/user/registration/validity-check',
        method: 'POST',
        config: { noToast: true },
        data: { email },
      }),
    }),

    checkTeamNameAvailability: builder.mutation<void, string>({
      query: (teamName) => ({
        api: 'auth',
        url: '/registration/tenant/availability/check',
        method: 'POST',
        config: { noToast: true },
        data: { team_name: teamName },
      }),
    }),

    signup: builder.mutation<void, SignUpRequest>({
      query: (data) => ({
        api: 'auth',
        url: '/registration/user/register',
        method: 'POST',
        config: { noToast: true },
        data,
      }),
    }),

    resendVerificationCode: builder.mutation<
      void,
      SignUpResendVerificationCodeRequest
    >({
      query: (data) => ({
        api: 'auth',
        url: '/registration/user/registration/resend-code',
        method: 'POST',
        config: { noToast: true },
        data,
      }),
    }),

    verifyCode: builder.mutation<SignUpVerifyCode, SignUpVerifyCodeRequest>({
      query: ({ email, verificationCode }) => ({
        api: 'auth',
        url: '/registration/user/identity/verify',
        method: 'POST',
        config: { noToast: true },
        data: {
          email,
          verification_code: verificationCode,
          verification_type: 'registration_code',
        },
      }),
      transformResponse: (response: SignUpVerifyCodeResponse) => ({
        registrationToken: response.registration_token,
      }),
      onQueryStarted: async (_, { queryFulfilled, dispatch }) => {
        const response = await queryFulfilled;

        if (!response.data.registrationToken) {
          return;
        }

        dispatch(
          updateSignupState({
            registrationToken: response.data.registrationToken,
          })
        );
      },
    }),

    resolveSSOToken: builder.mutation<
      SignUpResolveSSOToken,
      SignUpResolveSSOTokenRequest
    >({
      query: ({ code, origin, redirectUri }) => ({
        api: 'auth',
        url: `/registration/user/resolve-sso-token?sso_code=${code}&sso_origin=${origin}&redirect_uri=${redirectUri}`,
        method: 'GET',
        config: { noToast: true },
      }),
      transformResponse: (response: SignUpResolveSSOTokenResponse) => ({
        email: response.email,
        firstName: response.first_name,
        lastName: response.last_name,
        ssoToken: response.sso_token,
      }),
    }),

    tenantRegistration: builder.mutation<void, SignUpTenantRegistrationRequest>(
      {
        query: ({ registrationToken, ssoProvider, ...data }) => {
          const headers: Record<string, string> = {};

          if (
            ssoProvider === SignUpProvider.GITHUB ||
            ssoProvider === SignUpProvider.GOOGLE
          ) {
            headers['x-jfrog-sso-token'] = registrationToken;
            headers['x-jfrog-sso-origin'] = ssoProvider;
          }

          if (ssoProvider === SignUpProvider.EMAIL) {
            headers['x-jfrog-registration-token'] = registrationToken;
          }

          if (ssoProvider === SignUpProvider.ACCESS_TOKEN) {
            const accessToken = getAccessToken();
            if (accessToken) {
              headers['Authorization'] = `Bearer ${accessToken}`;
            }
          }

          return {
            api: 'auth',
            url:
              '/registration/tenant/register' +
              (window.location.hostname.includes('-internal')
                ? '?is_internal=true'
                : ''),
            method: 'POST',
            config: {
              noToast: true,
              headers,
            },
            data,
          };
        },
      }
    ),

    invitationRegistration: builder.mutation<
      void,
      SignUpInvitationRegistrationRequest
    >({
      query: ({
        firstName,
        lastName,
        ssoToken,
        ssoIdp,
        newPassword,
        invitationToken,
      }) => ({
        api: 'user',
        url: '/user/v1/register',
        method: 'POST',
        data: {
          first_name: firstName,
          last_name: lastName,
          ...(ssoIdp === SignUpProvider.EMAIL
            ? { new_password: newPassword }
            : {
                sso_token: ssoToken,
                sso_idp: ssoIdp,
              }),
        },
        config: {
          noToast: true,
          headers: {
            Authorization: `Bearer ${invitationToken}`,
          },
        },
      }),
    }),

    acceptInvitation: builder.mutation<void, string>({
      query: (invitationToken) => ({
        api: 'user',
        url: '/user/v1/accept-invitation',
        method: 'POST',
        config: {
          noToast: true,
          headers: { Authorization: `Bearer ${invitationToken}` },
        },
      }),
    }),
  }),
});

export const {
  useGetUserInfoQuery,

  useCheckEmailAvailabilityMutation,
  useCheckTeamNameAvailabilityMutation,
  useSignupMutation,
  useResendVerificationCodeMutation,
  useVerifyCodeMutation,
  useTenantRegistrationMutation,
  useResolveSSOTokenMutation,
  useVerifyInvitationMutation,
  useInvitationRegistrationMutation,
  useCheckRegistrationStatusMutation,
  useAcceptInvitationMutation,
} = signupService;

/**
 * 1. Message to Ido and Yaron to update design of invitation email
 * 2. Tell Yaron about the invalid email message for invitation sso
 * 3. Ask Igal for payload on team table
 * 4. clear sso flow data
 */
